/**
 * Derives the bare hunkware-api origin from a `VITE_HW_API_URL` value.
 *
 * ── Why this exists ────────────────────────────────────────────────────────────────────────────────
 *
 * `VITE_HW_API_URL` is **not the same shape in every environment**. Both booking apps set it, and:
 *
 *   booking-v2 testing:    https://hunkware-api.testing.apps.private.chhj.com/api
 *   booking-v2 production: https://hunkware-api.apps.chhj.com/api/v1/booking
 *   booking-v1 (all envs): https://hunkware-api…/api/v1/booking
 *
 * Some values are the bare `/api` mount, others are scoped to a domain prefix. Appending an absolute route
 * to the env value directly therefore yields `/api/v1/booking/v1/…` in production while working perfectly in
 * testing — a 404 that no lower environment reveals.
 *
 * Anything that needs to address a route OUTSIDE the app's own scoped prefix must go through here.
 *
 * The PR-override forms produced by `getApiUrl('hw_api', …)` are supported too, so `?hw_api_pr=123` testing
 * keeps working.
 */

/**
 * Strips everything from the `/api` segment onward, returning the scheme + host.
 *
 * Falls back to the input with a trailing slash removed if no `/api` segment is present — better a wrong-but-
 * visible URL than a thrown exception inside a booking flow.
 */
export const getHwApiOrigin = (hwApiUrl: string): string => {
  const trimmed = hwApiUrl.trim().replace(/\/+$/, '');
  const match = /^(.*?)(\/api(?:\/|$).*)$/.exec(trimmed);

  return match?.[1] ?? trimmed;
};

/**
 * Builds an absolute hunkware-api URL for a route that is mounted at the API root rather than inside the
 * calling app's scoped prefix.
 *
 * @param hwApiUrl - the raw `VITE_HW_API_URL` value (optionally already passed through `getApiUrl`)
 * @param route - absolute route including the global prefix, e.g. `/api/v1/public/website/subpartners/lookup`
 */
export const buildHwApiUrl = (hwApiUrl: string, route: string): string => {
  const origin = getHwApiOrigin(hwApiUrl);
  const path = route.startsWith('/') ? route : `/${route}`;

  return `${origin}${path}`;
};
